import { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import DataTableMUI from "../../components/common/DataTable";
import { DonorApplicationColumns } from "../../services/tableColomn";
import ViewDataPopup from "./viewDataPopup";
import { useNavigate } from "react-router-dom";
import {
  getAllDonorApplication,
  deleteDonorApplication,
  approveDonorApplication,
  rejectDonorApplication,
} from "../../redux/slices/donorApplication";
import {
  Dialog,
  DialogActions,
  Button,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import React, { forwardRef } from "react";
import logo from "../../assets/Logofile/logo (2).png";

const PdfFile = forwardRef(({ data }, ref) => (
  <div
    ref={ref}
    style={{
      width: "1122px", // A4 landscape at 96dpi
      height: "793px",
      background: "#fff",
      border: "8px solid #e3e8f7",
      borderRadius: "18px",
      padding: "40px 60px",
      fontFamily: "serif",
      position: "relative",
      boxSizing: "border-box",
    }}
  >
    <img
      src={logo}
      alt="Logo"
      style={{
        position: "absolute",
        top: 40,
        left: 60,
        width: 90,
        height: 90,
      }}
    />
    <div style={{ textAlign: "center", marginTop: 30 }}>
      <h1 style={{ color: "#3A6FF8", fontWeight: 700, margin: 0 }}>
        JEEVANDEEP ORGAN FOUNDATION
      </h1>
      <div style={{ color: "#bfa100", fontSize: 22, margin: "8px 0" }}>
        Certificate of Appreciation
      </div>
      <div style={{ color: "#222", fontSize: 18, margin: "18px 0 0" }}>
        This certificate is awarded to
      </div>
      <div
        style={{
          color: "#1976d2",
          fontWeight: 700,
          fontSize: 32,
          margin: "10px 0",
        }}
      >
        {data.fullName}
      </div>
      <div style={{ color: "#444", fontSize: 16, margin: "10px 0" }}>
        For their invaluable pledge towards organ donation and demonstrating
        compassion and commitment to saving lives.
      </div>
      <div style={{ margin: "30px 0 0" }}>
        <img
          src={data.image}
          alt="Donor"
          style={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            objectFit: "cover",
            border: "3px solid #e3e8f7",
            background: "#fff",
          }}
        />
      </div>
    </div>
    <div style={{ marginTop: 40, marginLeft: 60 }}>
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        <b>Gender:</b> {data.gender} &nbsp; <b>Blood Group:</b>{" "}
        {data.bloodGroup}
      </div>
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        <b>Date of Birth:</b> {data.dateOfBirth}
      </div>
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        <b>Mobile:</b> {data.mobileNumber} &nbsp; <b>Email:</b> {data.emailId}
      </div>
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        <b>Address:</b> {data.address}
      </div>
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        <b>Organs Pledged:</b>{" "}
        {Object.entries(data.organs || {})
          .filter(([k, v]) => v)
          .map(([k]) => k)
          .join(", ") || "-"}
      </div>
      <div style={{ fontSize: 16, marginBottom: 6 }}>
        <b>Tissues Pledged:</b>{" "}
        {Object.entries(data.tissues || {})
          .filter(([k, v]) => v)
          .map(([k]) => k)
          .join(", ") || "-"}
      </div>
    </div>
    <div
      style={{
        position: "absolute",
        left: 60,
        bottom: 60,
        fontSize: 14,
        color: "#888",
      }}
    >
      Authorized Signature
      <div style={{ borderTop: "1px solid #888", width: 180, marginTop: 8 }} />
    </div>
    <div
      style={{
        position: "absolute",
        right: 60,
        bottom: 60,
        fontSize: 14,
        color: "#888",
      }}
    >
      Founder, Jeevandeep
      <div style={{ borderTop: "1px solid #888", width: 180, marginTop: 8 }} />
    </div>
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 20,
        textAlign: "center",
        color: "#bfa100",
        fontStyle: "italic",
        fontSize: 13,
      }}
    >
      "The gift of organ donation is the greatest gift of all."
    </div>
  </div>
));

// Status filter options for the donor application table
const statusOptions = [
  { label: "All", value: "all", color: "#2563eb" },
  { label: "Pending", value: "pending", color: "#388e3c" },
  { label: "Approved", value: "approved", color: "#bfa100" },
  { label: "Rejected", value: "rejected", color: "#d32f2f" },
];

const DonorApplication = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [viewPopupOpen, setViewPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [pendingRejection, setPendingRejection] = useState(null);

  const { donorApplications, loading, statusCounts, pagination, error } =
    useAppSelector((state) => state.donorApplication);

  useEffect(() => {
    dispatch(
      getAllDonorApplication({
        status: statusFilter,
        page,
        search,
        limit: itemsPerPage,
        sortKey,
        sortOrder,
      })
    );
  }, [dispatch, statusFilter, page, search, itemsPerPage, sortKey, sortOrder]);

  const refetch = useCallback(
    () =>
      dispatch(
        getAllDonorApplication({
          status: statusFilter,
          page,
          search,
          limit: itemsPerPage,
          sortKey,
          sortOrder,
        })
      ),
    [dispatch, statusFilter, page, search, itemsPerPage, sortKey, sortOrder]
  );

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handleView = (row) => {
    setSelectedRow(row);
    setViewPopupOpen(true);
  };

  const handleEdit = (row) => {
    navigate(`/donor-application-form/${row._id}`);
  };

  const handleDelete = async (row) => {
    if (
      window.confirm("Are you sure you want to delete this donor application?")
    ) {
      try {
        await dispatch(deleteDonorApplication(row._id)).unwrap();
        refetch();
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  const handleSort = (col) => {
    if (!col.sortable) return;
    setSortKey(col.accessor);
    setSortOrder(
      sortKey === col.accessor && sortOrder === "asc" ? "desc" : "asc"
    );
    setPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleRejectClick = (row) => {
    setPendingRejection(row);
    setRejectDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!pendingApproval) return;

    try {
      await dispatch(
        approveDonorApplication({
          id: pendingApproval._id,
        })
      ).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to approve:", error);
    } finally {
      setDialogOpen(false);
      setPendingApproval(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingRejection) return;
    try {
      await dispatch(
        rejectDonorApplication({
          id: pendingRejection._id,
        })
      ).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to reject:", error);
    } finally {
      setRejectDialogOpen(false);
      setPendingRejection(null);
    }
  };

  const handleApproveCancel = () => {
    setDialogOpen(false);
    setPendingApproval(null);
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setPendingRejection(null);
  };

  // Add counts to statusOptions
  const statusOpts = statusOptions.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  // Add event listeners for focus and visibility change to refetch data when user returns
  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetch();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch]); // Now refetch is stable with useCallback

  // Show error message if API call fails
  useEffect(() => {
    if (error) {
      console.error("Donor Application API Error:", error);
      alert(`Error loading data: ${error}`);
    }
  }, [error]);

  return (
    <div className="data-table">
      <DataTableMUI
        title="Donor Application Gallery"
        columns={DonorApplicationColumns}
        data={donorApplications || []}
        extraData={{
          dispatch,
          refetch,
          onApproveClick: (row) => {
            console.log("Approve clicked for row:", row);
            setPendingApproval(row);
            setDialogOpen(true);
          },
          onRejectClick: handleRejectClick,
        }}
        statusOptions={statusOpts}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination
        itemsPerPage={itemsPerPage}
        currentPage={pagination?.page || 1}
        totalItems={pagination?.total || 0}
        setItemsPerPage={setItemsPerPage}
        onPageChange={handlePageChange}
        searchable
        searchTerm={searchInput}
        setSearchTerm={setSearchInput}
        onSearch={handleSearch}
        loading={loading}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSort={handleSort}
        draggable={true}
      />
      <Dialog
        open={dialogOpen}
        onClose={handleApproveCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Approval"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to approve{" "}
            {pendingApproval?.fullName || "this donor"}?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleApproveConfirm}
            color="primary"
            autoFocus
            variant="contained"
          >
            Yes, Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        aria-labelledby="alert-dialog-title-reject"
        aria-describedby="alert-dialog-description-reject"
      >
        <DialogTitle id="alert-dialog-title-reject">
          {"Confirm Rejection"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-reject">
            Are you sure you want to reject{" "}
            {pendingRejection?.fullName || "this donor"}?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="primary"
            autoFocus
            variant="contained"
          >
            Yes, Reject
          </Button>
        </DialogActions>
      </Dialog>
      {selectedRow && (
        <ViewDataPopup
          open={viewPopupOpen}
          onClose={() => {
            setViewPopupOpen(false);
            setSelectedRow(null);
          }}
          data={selectedRow}
        />
      )}
    </div>
  );
};

export default DonorApplication;
